/*
 * FROSTBEULE · Hero (owner: HOMEA) – <fb-hero> in sections/frost-hero.liquid
 * - Pointer parallax: every .fb-hero__layer[data-depth] follows the mouse with a damped spring
 *   (depth = px at the edge of the hero). Only motion 'full' + fine pointer; the rAF loop runs only
 *   while the spring moves and springs back to 0 when the pointer leaves.
 * - Snow: mounts window.FBSnow (frost-global.js) on [data-fb-hero-snow]; FBSnow itself pauses
 *   offscreen / in hidden tabs and respects data-motion.
 * The load choreography is pure CSS. Custom element = survives shopify:section:load/unload.
 */
(function () {
  'use strict';
  if (!window.customElements || window.customElements.get('fb-hero')) return;

  var root = document.documentElement;

  function motion() {
    if (window.FROSTBEULE && typeof window.FROSTBEULE.motion === 'function') return window.FROSTBEULE.motion();
    return root.getAttribute('data-motion') || 'full';
  }

  var finePointer = null;
  try {
    finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  } catch (e) {
    finePointer = null;
  }

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  class FbHero extends HTMLElement {
    connectedCallback() {
      this.layers = Array.prototype.map.call(this.querySelectorAll('.fb-hero__layer[data-depth]'), function (el) {
        return { el: el, depth: parseFloat(el.getAttribute('data-depth')) || 0 };
      });
      this.s = { x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0 };
      this.raf = 0;
      this.last = 0;

      this.onMove = this.onMove.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.onMotion = this.onMotion.bind(this);
      this.tick = this.tick.bind(this);

      if (this.hasAttribute('data-parallax') && this.layers.length) {
        this.addEventListener('pointermove', this.onMove, { passive: true });
        this.addEventListener('pointerleave', this.onLeave);
      }
      document.addEventListener('frost:motion-change', this.onMotion);

      this.mountSnow();
    }

    disconnectedCallback() {
      this.removeEventListener('pointermove', this.onMove);
      this.removeEventListener('pointerleave', this.onLeave);
      document.removeEventListener('frost:motion-change', this.onMotion);
      if (this.snowWait) window.removeEventListener('load', this.snowWait);
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.destroySnow();
    }

    canMove() {
      return motion() === 'full' && (!finePointer || finePointer.matches);
    }

    onMove(event) {
      if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      if (!this.canMove()) return;
      var r = this.getBoundingClientRect();
      if (!r.width || !r.height) return;
      this.s.tx = clamp(((event.clientX - r.left) / r.width - 0.5) * 2, -1, 1);
      this.s.ty = clamp(((event.clientY - r.top) / r.height - 0.5) * 2, -1, 1);
      this.start();
    }

    onLeave() {
      this.s.tx = 0;
      this.s.ty = 0;
      if (this.s.x || this.s.y) this.start();
    }

    start() {
      if (this.raf) return;
      this.last = performance.now();
      this.raf = window.requestAnimationFrame(this.tick);
    }

    tick(now) {
      var s = this.s;
      var dt = clamp((now - this.last) / 1000, 0.001, 0.032);
      this.last = now;
      // damped spring (k 90, c 14 → zeta ≈ 0.74): soft follow, small overshoot on release
      var ax = 90 * (s.tx - s.x) - 14 * s.vx;
      var ay = 90 * (s.ty - s.y) - 14 * s.vy;
      s.vx += ax * dt;
      s.vy += ay * dt;
      s.x += s.vx * dt;
      s.y += s.vy * dt;

      var settled =
        Math.abs(s.tx - s.x) < 0.0008 && Math.abs(s.ty - s.y) < 0.0008 && Math.abs(s.vx) < 0.002 && Math.abs(s.vy) < 0.002;
      if (settled) {
        s.x = s.tx;
        s.y = s.ty;
        s.vx = 0;
        s.vy = 0;
      }
      this.apply();
      this.raf = settled ? 0 : window.requestAnimationFrame(this.tick);
    }

    apply() {
      var s = this.s;
      for (var i = 0; i < this.layers.length; i++) {
        var l = this.layers[i];
        if (!s.x && !s.y) {
          l.el.style.removeProperty('transform');
        } else {
          l.el.style.transform =
            'translate3d(' + (s.x * l.depth).toFixed(2) + 'px,' + (s.y * l.depth * 0.6).toFixed(2) + 'px,0)';
        }
      }
    }

    reset() {
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.s = { x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0 };
      this.apply();
    }

    onMotion() {
      if (motion() !== 'full') this.reset();
    }

    mountSnow() {
      if (!this.hasAttribute('data-snow')) return;
      var el = this.querySelector('[data-fb-hero-snow]');
      if (!el) return;
      var self = this;
      var density = parseInt(this.getAttribute('data-snow-density'), 10);
      if (isNaN(density)) density = 45;
      function go() {
        if (self.snow || !self.isConnected) return true;
        var api = window.FBSnow;
        if (!api || typeof api.mount !== 'function') return false;
        self.snowEl = el;
        self.snow = api.mount(el, { density: density, wind: 0.25, color: '#ffffff' }) || true;
        return true;
      }
      if (!go()) {
        this.snowWait = function () {
          go();
        };
        window.addEventListener('load', this.snowWait, { once: true });
      }
    }

    destroySnow() {
      var h = this.snow;
      this.snow = null;
      if (!h) return;
      try {
        if (typeof h === 'function') h();
        else if (h && typeof h.destroy === 'function') h.destroy();
        else if (h && typeof h.unmount === 'function') h.unmount();
        else if (window.FBSnow && typeof window.FBSnow.unmount === 'function') window.FBSnow.unmount(this.snowEl);
      } catch (e) {
        /* snow already gone with the section */
      }
    }
  }

  window.customElements.define('fb-hero', FbHero);
})();
